import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Divider,
  Alert,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useDebounce } from 'use-debounce';
import doctorService, { DoctorSearchResult } from '../../services/doctorService';
import { usePharmacyStore } from '../../stores/pharmacyStore';

interface DoctorSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (doctor: DoctorSearchResult) => void;
  /**
   * PRX-80 Task 9 fix round 1 (item 14) — additive, optional: existing
   * callers (`ReimbursementTracker.tsx`, `CreateTaskDialog.tsx`) keep passing
   * it and see the exact same button. A caller with no "no doctor" concept
   * (`PrescriberAndClinicCard` — the care-provider API is upsert-only, there
   * is no "clear this prescriber" verb to call) omits it and the button is
   * not rendered, rather than showing an action that does nothing.
   */
  onSkip?: () => void;
  patientId?: string; // Kroll patient ID (numeric part)
}

const DoctorSelectionDialog: React.FC<DoctorSelectionDialogProps> = ({
  open,
  onClose,
  onSelect,
  onSkip,
  patientId,
}) => {
  const { activePharmacy } = usePharmacyStore();
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [patientDoctors, setPatientDoctors] = useState<DoctorSearchResult[]>([]);
  const [patientDoctorsLoading, setPatientDoctorsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<DoctorSearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(searchText, 500);

  // Fetch patient's doctors on open
  useEffect(() => {
    if (!open || !activePharmacy || !patientId) {
      setPatientDoctors([]);
      return;
    }

    const krollPatientId = patientId.includes('_')
      ? patientId.split('_')[1]
      : patientId;

    setPatientDoctorsLoading(true);
    doctorService
      .getDoctorsForPatient(activePharmacy.id, krollPatientId)
      .then((docs) => setPatientDoctors(docs))
      .catch(() => setPatientDoctors([]))
      .finally(() => setPatientDoctorsLoading(false));
  }, [open, activePharmacy, patientId]);

  // Search doctors
  const performSearch = useCallback(async () => {
    if (!activePharmacy || !debouncedSearch || debouncedSearch.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const raw = debouncedSearch.trim();
      let lastName = raw;
      let firstName = '';
      if (raw.includes(',')) {
        const parts = raw.split(',');
        lastName = parts[0].trim();
        firstName = parts.slice(1).join(',').trim();
      }

      const results = await doctorService.searchDoctors(activePharmacy.id, {
        lastName,
        firstName: firstName || undefined,
      });
      setSearchResults(results);
    } catch (err: any) {
      setError(err.message || 'Failed to search doctors');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, activePharmacy]);

  useEffect(() => {
    if (debouncedSearch && debouncedSearch.trim().length >= 2) {
      performSearch();
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearch, performSearch]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setSearchText('');
      setSearchResults([]);
      setError(null);
    }
  }, [open]);

  const renderDoctorItem = (doctor: DoctorSearchResult) => (
    <ListItemButton
      key={doctor.DoctorID}
      onClick={() => onSelect(doctor)}
      sx={{ borderRadius: 1, mb: 0.5 }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          {doctor.DoctorFirstName?.[0]}
          {doctor.DoctorLastName?.[0]}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={`Dr. ${doctor.DoctorLastName}, ${doctor.DoctorFirstName}`}
        secondary={doctor.CPSO ? `CPSO: ${doctor.CPSO}` : undefined}
      />
    </ListItemButton>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Select Doctor</DialogTitle>
      <DialogContent>
        {/* Patient's doctors section */}
        {patientId && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
              Patient's Doctors
            </Typography>
            {patientDoctorsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : patientDoctors.length > 0 ? (
              <List dense>
                {patientDoctors.map(renderDoctorItem)}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No doctors found for this patient
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Search section */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Search All Doctors
        </Typography>
        <TextField
          fullWidth
          placeholder="Last Name (or 'Last, First')"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading ? <CircularProgress size={20} /> : <SearchIcon />}
              </InputAdornment>
            ),
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
          {searchResults.map(renderDoctorItem)}
        </List>

        {!loading && searchResults.length === 0 && searchText.length >= 2 && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
            No doctors found matching "{searchText}"
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {onSkip && (
          <Button onClick={onSkip} color="inherit">
            Skip (No Doctor)
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DoctorSelectionDialog;
